import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import Product from '../components/product/Product';
import Message from '../components/Message';
import Loader from '../components/Loader';
import Paginate from '../components/Paginate';
// import { listProductsByCategory } from '../actions/productActions';

const CategoryProductsScreen = () => {
  const { category, priority, pageNumber = 1 } = useParams();

  const dispatch = useDispatch();

  const productCategoryList = useSelector((state) => state.productCategoryList);
  const { loading, error, products, page, pages } = productCategoryList;

  // Format the category name for display
  const formattedCategory = category ? category.replace(/-/g, ' ') : '';

  useEffect(() => {
    if (!category) return;
    
    // Load products immediately without async operations
    const loadCategoryProducts = () => {
      const adminProducts = localStorage.getItem('adminProducts');
      let allProducts = [];
      
      if (adminProducts) {
        try {
          allProducts = JSON.parse(adminProducts);
        } catch (error) {
          console.log('Error parsing admin products:', error);
        }
      }
      
      // Final fallback to sample data
      if (allProducts.length === 0) {
        allProducts = [
          {
            _id: '1',
            name: '3M Safety Goggles',
            category: 'Eye Protection',
            priority: 'high',
            description: 'Chemical splash and impact resistant safety goggles with anti-fog coating',
            price: 1200,
            countInStock: 200,
            images: ['/images/eye-protection-goggles.jpg'],
            brand: '3M',
            rating: 4.5,
            numReviews: 12
          },
          {
            _id: '2',
            name: 'Nitrile Examination Gloves (Box of 100)',
            category: 'Hand Protection',
            priority: 'high',
            description: 'Powder-free nitrile examination gloves for medical and laboratory use',
            price: 950,
            countInStock: 500,
            images: ['/images/nitrile-gloves.jpg'],
            brand: 'Top Glove',
            rating: 4.8,
            numReviews: 25
          },
          {
            _id: '3',
            name: '3M N95 Respirator Mask (Box of 20)',
            category: 'Respiratory Protection',
            priority: 'high',
            description: 'N95 respirator mask for protection against airborne particles',
            price: 1500,
            countInStock: 300,
            images: ['/images/n95-mask.jpg'],
            brand: '3M',
            rating: 4.7,
            numReviews: 18
          },
          {
            _id: '4',
            name: 'Noise Cancelling Ear Muffs',
            category: 'Hearing Protection',
            priority: 'high',
            description: 'Professional grade hearing protection with 30dB noise reduction',
            price: 800,
            countInStock: 150,
            images: ['/images/ear-muffs.jpg'],
            brand: 'Howard Leight',
            rating: 4.6,
            numReviews: 22
          },
          {
            _id: '5',
            name: 'Steel Toe Safety Boots',
            category: 'Foot Protection',
            priority: 'high',
            description: 'Durable steel toe boots with slip-resistant sole',
            price: 3500,
            countInStock: 75,
            images: ['/images/safety-boots.jpg'],
            brand: 'Caterpillar',
            rating: 4.4,
            numReviews: 18
          },
          {
            _id: '6',
            name: 'Disposable Coveralls (Pack of 25)',
            category: 'Disposables',
            priority: 'medium',
            description: 'Lightweight disposable coveralls for contamination protection',
            price: 1200,
            countInStock: 200,
            images: ['/images/coveralls.jpg'],
            brand: 'DuPont',
            rating: 4.2,
            numReviews: 15
          },
          {
            _id: '7',
            name: 'Chemical Resistant Suit',
            category: 'Chemical Suits',
            priority: 'high',
            description: 'Full body chemical protection suit with sealed seams',
            price: 4500,
            countInStock: 50,
            images: ['/images/chemical-suit.jpg'],
            brand: 'Lakeland',
            rating: 4.8,
            numReviews: 12
          },
          {
            _id: '8',
            name: 'Fire Retardant Jacket',
            category: 'Fire Protection',
            priority: 'high',
            description: 'Flame resistant jacket meeting NFPA standards',
            price: 5500,
            countInStock: 30,
            images: ['/images/fire-jacket.jpg'],
            brand: 'Bulwark',
            rating: 4.7,
            numReviews: 8
          }
        ];
      }
      
      // Filter by category and priority
      const filteredProducts = allProducts.filter(product => {
        const categoryMatch = product.category.toLowerCase() === formattedCategory.toLowerCase();
        const priorityMatch = !priority || priority === 'all' || product.priority === priority;
        return categoryMatch && priorityMatch;
      });
      
      // Simulate the Redux action
      dispatch({
        type: 'PRODUCT_CATEGORY_LIST_SUCCESS',
        payload: {
          products: filteredProducts,
          page: 1,
          pages: 1,
          total: filteredProducts.length
        }
      });
    };
    
    loadCategoryProducts();
  }, [dispatch, category, priority, pageNumber, formattedCategory]);

  // Category descriptions mapping
  const categoryDescriptions = {
    'Eye Protection': 'High-quality eye protection gear including safety glasses, goggles, and face shields to protect against hazards.',
    'Hand Protection': 'Protective gloves and hand safety equipment for various industrial and laboratory applications.',
    'Respiratory Protection': 'Respirators, masks, and breathing apparatus to protect against airborne hazards and contaminants.',
    'Hearing Protection': 'Ear plugs, ear muffs, and other hearing safety devices to prevent hearing damage in noisy environments.',
    'Foot Protection': 'Safety boots, shoes, and other foot protection gear designed for workplace safety.',
    'Disposables': 'Single-use protective items including gloves, masks, coveralls, and other disposable safety gear.',
    'Chemical Suits': 'Chemical resistant clothing and full-body protection suits for handling hazardous materials.',
    'Fire Protection': 'Fire-resistant clothing, equipment, and safety gear for fire hazard environments.',
    'Laboratory Equipment': 'Precision instruments and apparatus for scientific research and laboratory applications.',
    'Scientific Instruments': 'Specialized measurement and analysis tools for scientific and industrial use.'
  };

  return (
    <>
      <div className="mb-6">
        <Link to="/" className="text-primary hover:text-primary-dark">
          <i className="fas fa-arrow-left mr-2"></i>Back to Home
        </Link>
        <h1 className="text-2xl font-semibold mt-4 mb-2">{formattedCategory}</h1>
        <p className="text-gray-600 mb-6">
          {categoryDescriptions[formattedCategory] || `Browse our selection of ${formattedCategory} products.`}
        </p>
      </div>

      {loading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error}</Message>
      ) : products.length === 0 ? (
        <Message>No products found in this category</Message>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <div key={product._id} className="mb-6">
                <Product product={product} />
              </div>
            ))}
          </div>
          <Paginate 
            pages={pages} 
            page={page} 
            category={category} 
          />
        </>
      )}
    </>
  );
};

export default CategoryProductsScreen;
